/**
 * ContactNotesEditor — the team-authored notes timeline for ONE contact
 * (list newest-first, add, delete), with author + time on every entry.
 *
 * Extracted from ContactPanel so the inbox side panel and the contact detail
 * page share one implementation of the add/delete calls and the optimistic
 * list updates.
 *
 * `initialNotes` exists because the two hosts get their notes differently: the
 * detail page already received them inside GET /contacts/:id and must not
 * re-fetch, while the inbox panel has only a contact id and fetches here.
 *
 * Deliberately chrome-free (no Card/Section wrapper): each host supplies its
 * own container.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DeleteOutline } from '@mui/icons-material';
import adminApi, { ContactNote } from '@/services/admin.api';

interface ContactNotesEditorProps {
  contactId: string;
  /** Read-only when false: no composer. Also the fallback for `canDelete`
   *  when a host doesn't pass it. */
  canEdit: boolean;
  /** Whether THIS user may delete a note — DELETE is method-derived to 'full'
   *  server-side (require-permission.middleware.ts), a strictly higher tier
   *  than the 'edit' that gates adding one. Defaults to `canEdit` only for
   *  backward compatibility; a host should pass the real 'full'-level check. */
  canDelete?: boolean;
  /** Notes the host already has. Omit to fetch them here instead. */
  initialNotes?: ContactNote[];
  /** Live note count, for a host that renders one in its section header. */
  onCountChange?: (count: number) => void;
  /** Fired after a note is actually added or deleted (never on load), so a host
   *  showing an activity feed can refresh it. */
  onChanged?: () => void;
  /** Which store `contactId` lives in — same reasoning as ContactTagsEditor's
   *  `channel` prop. Defaults to 'whatsapp' for the Contacts detail page. */
  channel?: 'whatsapp' | 'instagram';
}

const ContactNotesEditor: React.FC<ContactNotesEditorProps> = ({
  contactId,
  canEdit,
  canDelete = canEdit,
  initialNotes,
  onCountChange,
  onChanged,
  channel = 'whatsapp',
}) => {
  const [notes, setNotes] = useState<ContactNote[]>(initialNotes || []);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  /** Note awaited a delete confirmation (id + a text preview for the dialog). */
  const [pendingDelete, setPendingDelete] = useState<{ id: string; preview: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  // `initialNotes === undefined` means "the host has no notes to give" (inbox
  // panel) — an empty array is a real, host-supplied value and must NOT trigger
  // a fetch, or the detail page would double-load on every contact with none.
  const hostSupplied = initialNotes !== undefined;
  // Held in a ref, not an effect dep: the detail page re-reads the contact
  // after every save, handing us a fresh array each time. Depending on its
  // IDENTITY would reset this list (dropping a note the user just added) on
  // an edit that had nothing to do with notes. Contact identity is the only
  // thing that should re-seed it.
  const initialNotesRef = useRef(initialNotes);
  initialNotesRef.current = initialNotes;
  const onCountChangeRef = useRef(onCountChange);
  onCountChangeRef.current = onCountChange;
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  useEffect(() => {
    if (hostSupplied) {
      setNotes(initialNotesRef.current || []);
      return;
    }
    let cancelled = false;
    setNotes([]);
    setDraft('');
    setError('');
    setLoading(true);
    const fetchNotes =
      channel === 'instagram'
        ? adminApi.getInstagramConversationNotes(contactId, { suppressPermissionLock: true })
        : adminApi.getContactNotes(contactId, { suppressPermissionLock: true });
    fetchNotes
      .then((list) => {
        if (!cancelled) setNotes(list);
      })
      .catch((e: any) => {
        if (!cancelled) setError(e?.response?.data?.error?.message || 'Failed to load notes');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [contactId, hostSupplied, channel]);

  useEffect(() => {
    onCountChangeRef.current?.(notes.length);
  }, [notes]);

  const addNote = async () => {
    const text = draft.trim();
    if (!text) return;
    try {
      setSaving(true);
      setError('');
      const note =
        channel === 'instagram'
          ? await adminApi.addInstagramConversationNote(contactId, text)
          : await adminApi.addContactNote(contactId, text);
      setNotes((prev) => [note, ...prev]);
      setDraft('');
      onChangedRef.current?.();
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e.message || 'Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const noteId = pendingDelete.id;
    try {
      setDeleting(true);
      if (channel === 'instagram') {
        await adminApi.deleteInstagramConversationNote(contactId, noteId);
      } else {
        await adminApi.deleteContactNote(contactId, noteId);
      }
      setNotes((prev) => prev.filter((n) => n._id !== noteId));
      setPendingDelete(null);
      onChangedRef.current?.();
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e.message || 'Failed to delete note');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      {canEdit && (
        <Box mb={1.5}>
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            placeholder="Add a note for your team…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            inputProps={{ maxLength: 2000 }}
          />
          <Box display="flex" justifyContent="flex-end" mt={0.75}>
            <Button size="small" variant="contained" onClick={addNote} disabled={saving || !draft.trim()}>
              {saving ? 'Adding…' : 'Add note'}
            </Button>
          </Box>
        </Box>
      )}
      {error && (
        <Typography variant="caption" color="error.main" display="block" mb={1}>
          {error}
        </Typography>
      )}
      {loading ? (
        <Box display="flex" justifyContent="center" py={1}>
          <CircularProgress size={18} />
        </Box>
      ) : notes.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No notes yet.
        </Typography>
      ) : (
        <Stack spacing={1.25}>
          {notes.map((n) => (
            <Box
              key={n._id}
              sx={{ p: 1.25, borderRadius: 1.5, bgcolor: 'grey.50', border: '1px solid #eef2f7' }}
            >
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {n.text}
              </Typography>
              <Box display="flex" alignItems="center" justifyContent="space-between" mt={0.5}>
                <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {n.authorName} · {new Date(n.createdAt).toLocaleString()}
                  </Typography>
                  {/* The author of an imported note is whoever ran the import,
                      and the timestamp is when the file was loaded — neither is
                      when the observation was actually made. The chip keeps the
                      byline from being read as something it is not. Imported
                      notes also deliberately do NOT mark a contact as
                      Contacted (see the backend's contact-query-builder). */}
                  {n.source === 'import' && (
                    <Tooltip title="Came from a contact import — not a note typed here">
                      <Chip label="Imported" size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                    </Tooltip>
                  )}
                </Box>
                {canDelete && (
                  <Tooltip title="Delete note">
                    <IconButton
                      size="small"
                      onClick={() =>
                        setPendingDelete({
                          id: n._id,
                          preview: n.text.length > 140 ? `${n.text.slice(0, 140)}…` : n.text,
                        })
                      }
                    >
                      <DeleteOutline fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          ))}
        </Stack>
      )}

      <Dialog open={!!pendingDelete} onClose={() => setPendingDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete note?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            This cannot be undone.
          </Typography>
          {pendingDelete && (
            <Typography
              variant="body2"
              sx={{
                p: 1.25,
                borderRadius: 1.5,
                bgcolor: 'grey.50',
                border: '1px solid #eef2f7',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {pendingDelete.preview}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={confirmDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ContactNotesEditor;
